import type { HTMLAttributes, PropsWithChildren } from "react";
import { forwardRef } from "react";
import { cn } from "@/lib/utils";
import { useUserSettingsContext } from "../contexts/user-settings-context";
import { PanelVariantProvider } from "./panel-variant-context";

type PanelProps = PropsWithChildren<{
  variant?: "frosted" | "flat";
  className?: string;
  attach?: "top" | "right" | "bottom" | "left" | Array<"top" | "right" | "bottom" | "left">;
}> & Pick<HTMLAttributes<HTMLDivElement>, "aria-label" | "role">;

function sideClasses(attach?: PanelProps["attach"]) {
  const sides = Array.isArray(attach) ? attach : attach ? [attach] : [];
  const borderMap: Record<string, string> = {
    top: "border-t-0",
    right: "border-r-0",
    bottom: "border-b-0",
    left: "border-l-0",
  };
  const radiusMap: Record<string, string> = {
    top: "rounded-t-none",
    right: "rounded-r-none",
    bottom: "rounded-b-none",
    left: "rounded-l-none",
  };
  const borders = sides.map((s) => borderMap[s]).filter(Boolean).join(" ");
  const radii = sides.map((s) => radiusMap[s]).filter(Boolean).join(" ");
  return { border: borders, radius: radii };
}

const Panel = forwardRef<HTMLDivElement, PanelProps>(
({ children, variant, className = "", attach, role, ...aria }, ref) => {
  const { uiVariant } = useUserSettingsContext();
  const effectiveVariant = (variant ?? uiVariant ?? 'flat');
  const { border: borderClasses, radius: radiusClasses } = sideClasses(attach);
  const attachClasses = [borderClasses, radiusClasses].filter(Boolean).join(" ");

  if (effectiveVariant === "flat") {
    return (
      <PanelVariantProvider value={effectiveVariant}>
        <div className={cn(
          "rounded-lg md:rounded-lg border border-black/10 bg-card shadow-md p-1 overflow-hidden",
          attachClasses,
          className
        )} role={role} ref={ref} {...aria}>
          {children}
        </div>
      </PanelVariantProvider>
    );
  }

  return (
    <PanelVariantProvider value={effectiveVariant}>
      <div className={cn(
        "relative isolate bg-transparent rounded-[1rem] md:rounded-[1rem] shadow-lg",
        radiusClasses,
        className
      )} role={role} ref={ref} {...aria}>
        <div
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute inset-0 z-0 rounded-[inherit] border-2 border-card/20 bg-background/60 bg-opacity-60 backdrop-blur-[1px]",
            borderClasses
          )}
        />
        <div className="relative z-10 rounded-[inherit] bg-background/60 bg-opacity-60 p-2">
          {children}
        </div>
      </div>
    </PanelVariantProvider>
  );
});

Panel.displayName = "Panel";

export default Panel;
